import { ReactElement, useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { TimeUnit, TooltipItem } from "chart.js";
import { useDependency } from "../../../../contexts/DependencyContext";
import { DateService } from "../../../../services/types/DateService";
import { IndicatorStatusHistoryService } from "../../../../services/http/indicatorstatushistory/IndicatorStatusHistoryService";
import { GetIndicatorEventsParameters } from "../../../../services/http/indicatorstatushistory/models/GetIndicatorEventsParameters";
import { IIndicatorEvent } from "../../../../services/http/indicatorstatushistory/models/IIndicatorEvent";
import { GetIndicatorStatusHistoryByPointsParameters } from "../../../../services/http/indicatorstatushistory/models/GetIndicatorStatusHistoryByPointsParameters";
import { INumericTimeSeriesPoints } from "../../../../services/http/indicatorstatushistory/models/INumericTimeSeriesPoints";
import { IStatusHistoryConfiguration } from "../../../../services/http/widget/models/IStatusHistoryConfiguration";
import { IWidget } from "../../../../services/http/widget/models/IWidget";
import LineChart, { ILineChartSeries, ChartPointsPerPixelRatio } from "../../../../components/Shared/LineChart";
import Spinner from "../../../../components/Shared/Spinner";
import { IDateRange } from "../DateFilters";
import IndicatorEventsTable from "./IndicatorEventsTable";

interface IStatusHistoryFullScreenProps
{
    widget: IWidget;
    dateFilters: IDateRange[];
}

function getTimeUnit(from: Date, to: Date): TimeUnit
{
    const ms = to.getTime() - from.getTime();
    const sec = ms / 1000;
    const minute = sec / 60;
    const hour = minute / 60;
    const day = hour / 24;

    if (ms < 100)
        return 'millisecond';

    if (sec < 100)
        return 'second';

    if (minute < 100)
        return 'minute';

    if (hour < 100)
        return 'hour';

    if (day < 100)
        return 'day';

    return 'week';
}

async function loadEvents(service: IndicatorStatusHistoryService, componentId: number, indicatorId: number, dateFilters: IDateRange[]): Promise<IIndicatorEvent[]>
{
    if (dateFilters.length === 0)
        return [];

    const fromDate = dateFilters[0].from;
    const toDate = dateFilters[0].to;

    if (fromDate === null || toDate === null) return [];

    const parameters = new GetIndicatorEventsParameters();
    parameters.componentId = componentId;
    parameters.indicatorId = indicatorId;
    parameters.fromDate = new Date(fromDate);
    parameters.toDate = new Date(toDate);

    return await service.getIndicatorEvents(parameters);
}

async function loadTimeSeriesPoints(service: IndicatorStatusHistoryService, componentId: number, indicatorId: number, dateFilters: IDateRange[], maxPoints: number): Promise<INumericTimeSeriesPoints[]>
{
    if (dateFilters.length === 0 || maxPoints === 0) return [];

    const result: INumericTimeSeriesPoints[] = [];

    for (let i = 0; i < dateFilters.length; i++)
    {
        const dateFilter = dateFilters[i];
        const fromDate = dateFilter.from;
        const toDate = dateFilter.to;

        if (fromDate === null || toDate === null || maxPoints <= 1) continue;

        const parameters = new GetIndicatorStatusHistoryByPointsParameters();
        parameters.componentId = componentId;
        parameters.indicatorId = indicatorId;
        parameters.fromDate = fromDate;
        parameters.toDate = toDate;
        parameters.maxPoints = maxPoints;

        result.push(await service.getNumericTimeSeriesPoints(parameters));
    }

    return result;
}

function StatusHistoryFullScreen(props: IStatusHistoryFullScreenProps): ReactElement
{
    const service = useDependency(IndicatorStatusHistoryService);
    const dateService = useDependency(DateService);
    const chartContainerRef = useRef<HTMLDivElement>(null);
    const lastDateFilters = useRef<IDateRange[]>([]);
    const timeSeriesPoints = useRef<INumericTimeSeriesPoints[]>([]);
    const [chartSeries, setChartSeries] = useState<ILineChartSeries[]>([]);
    const [events, setEvents] = useState<IIndicatorEvent[]>([]);
    const configuration = JSON.parse(props.widget.uiConfiguration) as IStatusHistoryConfiguration;
    const queryString = new URLSearchParams(useLocation().search);
    const indicatorId = queryString.has('id') ? Number(queryString.get('id')) : configuration.indicatorId;
    const indicatorName = queryString.has('name') ? `${queryString.get('name')} history` : "Status History";
    const maxPoints = chartContainerRef.current ? Math.round(chartContainerRef.current.offsetWidth * ChartPointsPerPixelRatio) : 1;
    const timeUnit = props.dateFilters &&
        props.dateFilters.length >= 1 &&
        props.dateFilters[0].from &&
        props.dateFilters[0].to
        ? getTimeUnit(props.dateFilters[0].from, props.dateFilters[0].to)
        : 'day';

    const getChartSeries = useCallback((): ILineChartSeries[] =>
    {
        return timeSeriesPoints.current
            .map(x =>
            {
                const { dateFrom, dateTo, values } = x;
                const range = dateService.getDateRange(dateFrom, dateTo, values.length);
                const title = `${indicatorName} ${dateService.format(new Date(dateFrom), 'M/d/yy HH:mm')} - ${dateService.format(new Date(dateTo), 'M/d/yy HH:mm')}`;
                (x as any)['dateRange'] = range;

                return {
                    title,
                    labels: range.map(y => y.toJSON()),
                    values: x.values.map(y => y !== null ? y * 100 : null)
                } as ILineChartSeries;
            });
    }, [dateService, indicatorName]);

    const getTooltipLabel = useCallback((tooltipItem: TooltipItem<"line">): string =>
    {
        const series = timeSeriesPoints.current;

        if (!series || series.length === 0 || !tooltipItem || tooltipItem.datasetIndex === undefined || tooltipItem.dataIndex === undefined)
            return '';

        const range = (series[tooltipItem.datasetIndex] as any)['dateRange'];
        const fixedRawValue = Number(tooltipItem.raw)?.toFixed(5);
        const yValue = parseFloat(fixedRawValue);
        const xValue = dateService.format(new Date(range[tooltipItem.dataIndex]), 'MM/dd/yyyy HH:mm:ss');

        return `${yValue.toString()} - ${xValue}`;
    }, [dateService])

    const getTooltipTitle = useCallback((_: any[]): string => "", []);

    useEffect(() =>
    {
        if (props.dateFilters.length > 0 && JSON.stringify(lastDateFilters.current) !== JSON.stringify(props.dateFilters))
        {
            (async () =>
            {
                timeSeriesPoints.current = [];
                setChartSeries([]);
                timeSeriesPoints.current = await loadTimeSeriesPoints(service, configuration.componentId, indicatorId, props.dateFilters, maxPoints);
                const eventsCall = await loadEvents(service, configuration.componentId, indicatorId, props.dateFilters);
                if (timeSeriesPoints.current.length > 0 && eventsCall)
                {
                    lastDateFilters.current = props.dateFilters;
                    setChartSeries(getChartSeries());
                    setEvents(eventsCall);
                }
            })()
        }
    }, [props.dateFilters, getChartSeries, configuration.componentId, configuration.indicatorId, maxPoints, service, indicatorId])

    return <>
        <div ref={chartContainerRef}>
            {chartSeries.length === 0 && <Spinner caption="Loading data..." />}
            {chartSeries.length > 0 && <LineChart
                series={chartSeries}
                yLabel="Percentage (%)"
                getTooltipLabel={getTooltipLabel}
                getTooltipTitle={getTooltipTitle}
                timeUnit={timeUnit}
                size="medium"
                displayLegend
                enableZoom
            />}
        </div>

        {events.length === 0 && <Spinner caption="Loading events..." />}
        {events.length > 0 && <IndicatorEventsTable events={events} />}
    </>;
}

export default StatusHistoryFullScreen;